import { motion } from "framer-motion";
import { MessageSquare, FileSearch, Sparkles, Megaphone, Handshake, Banknote } from "lucide-react";

const STEPS = [
  { icon: MessageSquare, title: "Consultation", desc: "A no-pressure conversation to understand your family, the estate, and your goals." },
  { icon: FileSearch, title: "Probate Review", desc: "We analyze the will, court status, and identify the fastest legal pathway." },
  { icon: Sparkles, title: "Property Preparation", desc: "Cleaning, staging, repairs — coordinated by our vetted vendor network." },
  { icon: Megaphone, title: "Marketing", desc: "Cinematic media, paid digital, and our buyer database get the home seen." },
  { icon: Handshake, title: "Offers & Sale", desc: "Court-compliant offers, negotiation, and overbid processes — handled expertly." },
  { icon: Banknote, title: "Distribution", desc: "Net proceeds delivered to heirs with full transparency and reporting." },
];

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="relative py-24 sm:py-32 bg-navy-900 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }} />
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-gold-500/10 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-gold-600/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="section-overline text-gold-400">The Process</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-white leading-tight tracking-tight">
            Six elegant steps. <em className="italic text-gold-400 font-medium">Zero surprises.</em>
          </h2>
        </div>

        {/* Desktop timeline */}
        <div className="hidden lg:block relative">
          <div className="absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/40 to-transparent" />
          <div className="grid grid-cols-6 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`process-step-${i}`}
                className="relative"
              >
                <div className="relative flex justify-center mb-6">
                  <div className="relative h-24 w-24 rounded-full bg-navy-800 border-2 border-gold-500/40 flex items-center justify-center group-hover:border-gold-500 transition-all">
                    <s.icon size={28} className="text-gold-400" />
                    <div className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-gold-500 text-navy-900 text-xs font-bold flex items-center justify-center">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="font-heading text-lg text-white">{s.title}</h3>
                  <p className="mt-2 text-xs text-white/60 leading-relaxed px-2">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile/tablet timeline */}
        <div className="lg:hidden space-y-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`process-step-mobile-${i}`}
              className="flex gap-5 items-start"
            >
              <div className="relative shrink-0">
                <div className="h-16 w-16 rounded-full bg-navy-800 border-2 border-gold-500/40 flex items-center justify-center">
                  <s.icon size={22} className="text-gold-400" />
                </div>
                {i < STEPS.length - 1 && (
                  <div className="absolute left-1/2 top-16 w-px h-12 bg-gold-500/30" />
                )}
              </div>
              <div className="flex-1 pt-1">
                <div className="text-[10px] text-gold-400 font-semibold tracking-[0.2em] uppercase">Step {i + 1}</div>
                <h3 className="font-heading text-lg text-white mt-0.5">{s.title}</h3>
                <p className="mt-1 text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
